// macadamia-app/src/components/pages/jurisprudences/JurisprudencesSearch.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../utils/apiClient';
import Sparkles from '../../../macadamia-core/icons/Sparkles';
import SearchInput from './SearchInput';
import JurisprudenceModalWrapper from './JurisprudenceModalWrapper';
import FollowButton from './FollowButton';
import FiltersModal from './FiltersModal';

interface Jurisprudence {
    id: string;
    date: string;
    court: string;
    chamber?: string;
    number: string;
    jurisdiction: string;
    solution: 'rejet' | 'cassation' | 'admission';
    mainQuestions: string[];
    mainDomain: string;
    subDomains: string[];
    code: string;
    codeNumber: string;
    similarCases: number;
    isFollowed: boolean;
}

interface ApiJurisprudence {
    id: string;
    jurisdiction: string;
    juridictionNom?: string; // Nom propre depuis collection juridictions
    location?: string;
    chamber: string;
    number: string;
    solution: string;
    themes: string[];
    decision_date: string;
    title: string;
    summary: string;
    hasMetadata: boolean;
    primary_issues: string[];
    issue_taxonomy: {
        primary_issues?: string[];
        legal_basis?: {
            articles: string[];
            code: string;
        };
        comparable_issues?: string[];
    };
    metadata_confidence: number | null;
    sub_domains: string[];
    primary_domain: string;
    similar_cases_count: number;
    isFollowed: boolean;
}

interface ApiResponse {
    success: boolean;
    data: ApiJurisprudence[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
    filters_applied: {
        query: string | null;
        mainDomains: string[] | null;
        subDomains: string[] | null;
        jurisdictions: string[] | null;
        chambers: string[] | null;
        solutions: string[] | null;
        hasMetadataOnly: boolean;
        isInitialLoad?: boolean;
    };
    metadata: {
        results_with_metadata: number;
        enrichment_rate_percent: number;
        execution_time: string;
        filters_count?: number;
        performance_notes: {
            pipeline_stages: number;
            load_type?: string;
            strategy?: string;
        };
    };
}

// Interface ActiveFilter mise à jour pour correspondre au FiltersModal optimisé
interface ActiveFilter {
    type: 'mainDomain' | 'subDomain' | 'jurisdiction';
    value: string;
    label: string;
}

// Composant squelette pour la grille (chargement initial ET filtres)
const JurisprudenceGridSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(9)].map((_, i) => (
            <div
                key={i}
                className="bg-white dark:bg-mcdm-third-300 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 animate-pulse"
            >
                {/* Titre */}
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>

                {/* Juridiction et chambre */}
                <div className="space-y-2 mb-4">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>

                {/* Bouton suivre */}
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-16 mb-4"></div>

                {/* Solution */}
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>

                {/* Questions principales */}
                <div className="space-y-2 mb-4">
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-4/5"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/5"></div>
                </div>

                {/* Domaines */}
                <div className="space-y-3 mb-4">
                    <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                    <div className="flex gap-2">
                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
                    </div>
                </div>

                {/* Pied de carte */}
                <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
                </div>
            </div>
        ))}
    </div>
);

const JurisprudencesSearch = () => {

    const [searchTerm, setSearchTerm] = useState('');
    const [jurisprudences, setJurisprudences] = useState<Jurisprudence[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
    const [isReplaceLoading, setIsReplaceLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
    const [showFollowedOnly, setShowFollowedOnly] = useState(false);
    const [selectedJurisprudenceId, setSelectedJurisprudenceId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

    const [, setApiStats] = useState<{
        totalResults: number;
        resultsWithMetadata: number;
        enrichmentRate: number;
    } | null>(null);

    const navigate = useNavigate();

    // Fonctions utilitaires (inchangées)
    const normalizeFilterValue = useCallback((value: string) => {
        if (typeof value !== 'string') {
            console.warn('normalizeFilterValue: valeur non-string reçue:', value);
            return String(value || '').toLowerCase().trim();
        }
        return value.toLowerCase().trim();
    }, []);

    const toggleFilter = useCallback((filter: ActiveFilter) => {
        setActiveFilters(prev => {
            const normalizedValue = normalizeFilterValue(filter.value);
            const existingIndex = prev.findIndex(f =>
                f.type === filter.type &&
                normalizeFilterValue(f.value) === normalizedValue
            );

            if (existingIndex !== -1) {
                return prev.filter((_, index) => index !== existingIndex);
            } else {
                return [...prev, filter];
            }
        });
    }, [normalizeFilterValue]);

    const removeFilter = useCallback((filterToRemove: ActiveFilter) => {
        setActiveFilters(prev =>
            prev.filter(f => !(f.type === filterToRemove.type && f.value === filterToRemove.value))
        );
    }, []);

    const isFilterActive = useCallback((type: ActiveFilter['type'], value: string) => {
        const normalizedValue = normalizeFilterValue(value);
        return activeFilters.some(f =>
            f.type === type &&
            normalizeFilterValue(f.value) === normalizedValue
        );
    }, [activeFilters, normalizeFilterValue]);

    const buildFilterParams = useCallback((filters: ActiveFilter[]) => {
        const params: Record<string, string[]> = {
            mainDomains: [],
            subDomains: [],
            jurisdictions: [],
            chambers: [],
            solutions: []
        };

        filters.forEach(filter => {
            switch (filter.type) {
                case 'mainDomain':
                    params.mainDomains.push(filter.value);
                    break;
                case 'subDomain':
                    params.subDomains.push(filter.value);
                    break;
                case 'jurisdiction':
                    params.jurisdictions.push(filter.value);
                    break;
            }
        });

        return params;
    }, []);

    const mapApiDataToJurisprudence = useCallback((apiData: ApiJurisprudence): Jurisprudence => {
        try {
            const mapped = {
                id: apiData.id,
                date: new Date(apiData.decision_date).toLocaleDateString('fr-FR'),
                court: apiData.juridictionNom || apiData.jurisdiction,
                chamber: apiData.chamber || '',
                number: apiData.number,
                jurisdiction: apiData.jurisdiction,
                solution: (apiData.solution || 'rejet').toLowerCase() as 'rejet' | 'cassation' | 'admission',
                mainQuestions: apiData.primary_issues || [],
                mainDomain: apiData.hasMetadata && apiData.primary_domain
                    ? apiData.primary_domain
                    : (apiData.issue_taxonomy?.legal_basis?.code ||
                        (apiData.themes && apiData.themes.length > 0 ? apiData.themes[0] : '') ||
                        'Non spécifié'),
                subDomains: (apiData.hasMetadata && apiData.sub_domains && apiData.sub_domains.length > 0
                    ? apiData.sub_domains
                    : apiData.themes || []).filter(domain => domain && typeof domain === 'string'),
                code: apiData.issue_taxonomy?.legal_basis?.code || 'Non spécifié',
                codeNumber: apiData.issue_taxonomy?.legal_basis?.articles?.[0] || 'N/A',
                similarCases: apiData.hasMetadata ? apiData.similar_cases_count : Math.floor(Math.random() * 15) + 1,
                isFollowed: apiData.isFollowed || false
            };

            return mapped;
        } catch (error) {
            console.error(`Erreur mapping élément ${apiData.id}:`, error);
            throw error;
        }
    }, []);

    // Fonction pour gérer les changements de suivi depuis le FollowButton
    const handleFollowChange = useCallback((id: string, isFollowed: boolean) => {
        // Mettre à jour directement les jurisprudences en mémoire
        setJurisprudences(prev =>
            prev.map(jurisprudence =>
                jurisprudence.id === id
                    ? { ...jurisprudence, isFollowed }
                    : jurisprudence
            )
        );
    }, []);

    // FONCTION UNIQUE SIMPLIFIÉE pour tous les chargements
    const fetchJurisprudences = useCallback(async (
        searchQuery: string = '',
        pageNum: number = 1,
        append: boolean = false,
        filters: ActiveFilter[] = []
    ) => {
        try {
            const isInitialLoad = pageNum === 1 && !searchQuery && filters.length === 0;

            setLoading(true);

            // Afficher le skeleton seulement si on remplace les données (pas pour "charger plus")
            if (!append) {
                setIsInitialLoading(isInitialLoad);
                setIsReplaceLoading(true);
            }

            const baseParams = {
                page: pageNum.toString(),
                limit: '30',
                ...(searchQuery && { query: searchQuery })
            };

            const filterParams = buildFilterParams(filters);
            const allParams: Record<string, string> = { ...baseParams };

            Object.entries(filterParams).forEach(([key, values]) => {
                if (values.length > 0) {
                    allParams[key] = values.join(',');
                }
            });

            const params = new URLSearchParams(allParams);

            // SYSTÈME UNIFIÉ : Bascule intelligente
            let path = '';

            if (searchQuery && searchQuery.trim().length > 0) {
                // 1. RECHERCHE ACTIVE -> Endpoint Unifié "Damia" (Direct Lookup + AI)
                path = `/jurisprudences/search/unified?${params}`;
            } else {
                // 2. NAVIGATION GRILLE (Filtres seuls) -> Endpoint Grid optimisé
                path = `/jurisprudences/grid?${params}`;
            }

            const response = await apiClient(path);

            if (!response.ok) {
                // Gestion spécifique 404 pour recherche directe échouée (si lookup 0 résultats)
                if (response.status === 404 && searchQuery) {
                    setJurisprudences([]);
                    setHasMore(false);
                    setApiStats(null);
                    setLoading(false);
                    setIsInitialLoading(false);
                    setIsReplaceLoading(false);
                    return;
                }

                if (response.status === 401) {
                    navigate('/login');
                    return;
                }
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const data: ApiResponse = await response.json();

            if (data.success && data.data) {
                const mappedData = data.data.map(mapApiDataToJurisprudence);

                if (append) {
                    setJurisprudences(prev => [...prev, ...mappedData]);
                } else {
                    setJurisprudences(mappedData);
                }

                setHasMore(data.pagination.hasNextPage);
                setPage(pageNum);

                setApiStats({
                    totalResults: data.pagination.total,
                    resultsWithMetadata: data.metadata.results_with_metadata,
                    enrichmentRate: data.metadata.enrichment_rate_percent
                });
            } else {
                console.error('Réponse API invalide:', data);
                throw new Error('Réponse API invalide');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des jurisprudences:', error);
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
            setIsReplaceLoading(false);
        }
    }, [navigate, buildFilterParams, mapApiDataToJurisprudence]);

    // CHARGEMENT INITIAL SIMPLIFIÉ (un seul useEffect)
    useEffect(() => {
        fetchJurisprudences('', 1, false, []);
    }, []); // Chargement initial une seule fois

    // useEffect pour les filtres (déclenche une nouvelle recherche)
    useEffect(() => {
        fetchJurisprudences(searchTerm, 1, false, activeFilters);
    }, [activeFilters]);

    const handleSearch = useCallback((newSearchTerm: string) => {
        setSearchTerm(newSearchTerm);
        setPage(1);
        fetchJurisprudences(newSearchTerm, 1, false, activeFilters);
    }, [fetchJurisprudences, activeFilters]);

    const loadMore = useCallback(() => {
        if (hasMore && !loading) {
            fetchJurisprudences(searchTerm, page + 1, true, activeFilters);
        }
    }, [hasMore, loading, searchTerm, page, activeFilters, fetchJurisprudences]);

    const openJurisprudenceModal = (jurisprudenceId: string) => {
        setSelectedJurisprudenceId(jurisprudenceId);
        setIsModalOpen(true);
    };

    const closeJurisprudenceModal = () => {
        setIsModalOpen(false);
        setSelectedJurisprudenceId(null);
    };

    const clearSearch = () => {
        handleSearch('');
    };

    const getSolutionColor = (solution: string) => {
        switch (solution) {
            case 'rejet':
                return 'text-red-600 dark:text-red-400';
            case 'cassation':
                return 'text-orange-600 dark:text-orange-400';
            case 'admission':
                return 'text-green-600 dark:text-green-400';
            default:
                return 'text-gray-600 dark:text-gray-400';
        }
    };

    return (
        <div className="space-y-6">
            {/* Barre de recherche principale */}
            <SearchInput onSearch={handleSearch} />

            {/* Boutons de contrôle et filtres actifs */}
            <div className="flex flex-wrap items-center gap-3">
                <style>{`
                    .active-search-chip-glow {
                        position: relative;
                        z-index: 0;
                        overflow: hidden;
                    }
                    .active-search-chip-glow::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        z-index: -1;
                        background: linear-gradient(90deg, #FF6778, #FFBA71, #8D99FF, #F5B9EA, #FF6778);
                        background-size: 300% 100%;
                        animation: gradientFlow 8s ease infinite;
                        opacity: 1;
                        border-radius: 9999px;
                    }
                    /* Background overlay to create the border effect */
                    .active-search-chip-content {
                        margin: 1.5px; /* Width of the border */
                        background-color: white; /* Default light mode */
                        border-radius: 9999px;
                        height: calc(100% - 3px);
                        display: flex;
                        align-items: center;
                    }
                    .dark .active-search-chip-content {
                        background-color: #1F2937; /* Match dark:bg-mcdm-third-300 approx */
                    }
                    @keyframes gradientFlow {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                `}</style>
                <div className="flex gap-3">
                    <button
                        onClick={() => setIsFiltersModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-mcdm-third-300 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-mcdm-third-400 transition-colors duration-200">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="3" y="6" width="18" height="2" rx="1" />
                            <rect x="6" y="11" width="12" height="2" rx="1" />
                            <rect x="9" y="16" width="6" height="2" rx="1" />
                        </svg>
                        Filtres
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-mcdm-third-300 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-mcdm-third-400 transition-colors duration-200">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                        </svg>
                        Trier
                    </button>
                    <button
                        onClick={() => setShowFollowedOnly(!showFollowedOnly)}
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors duration-200 ${showFollowedOnly
                            ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900 dark:border-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-mcdm-third-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-mcdm-third-400'
                            }`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {showFollowedOnly ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                        </svg>
                        Suivies seulement
                    </button>
                </div>

                {/* Filtres actifs et Recherche Active */}
                {(activeFilters.length > 0 || (searchTerm && searchTerm.length > 0)) && (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">Filtres actifs :</span>

                        {/* Puce pour la recherche textuelle active - STYLE PREMIUM */}
                        {searchTerm && (
                            <span className="active-search-chip-glow rounded-full">
                                <span className="active-search-chip-content px-3 py-1 gap-1">
                                    <span className="flex items-center justify-center">
                                        <Sparkles width="16" height="16" stroke="#9da3af" />
                                    </span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100 mx-1">
                                        {searchTerm}
                                    </span>
                                    <button
                                        onClick={clearSearch}
                                        className="ml-1 p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200 text-gray-500 dark:text-gray-400"
                                        title="Effacer la recherche"
                                    >
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </span>
                            </span>
                        )}
                        {activeFilters.map((filter, index) => {
                            const isMainDomain = filter.type === 'mainDomain';
                            const isSubDomain = filter.type === 'subDomain';
                            const isJurisdiction = filter.type === 'jurisdiction';

                            let colorClasses, textColorClasses, hoverClasses, typeLabel;

                            if (isMainDomain) {
                                colorClasses = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-700';
                                textColorClasses = 'text-blue-600 dark:text-blue-300';
                                hoverClasses = 'hover:bg-blue-200 dark:hover:bg-blue-800';
                                typeLabel = 'Domaine';
                            } else if (isSubDomain) {
                                colorClasses = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-700';
                                textColorClasses = 'text-green-600 dark:text-green-300';
                                hoverClasses = 'hover:bg-green-200 dark:hover:bg-green-800';
                                typeLabel = 'Sous-domaine';
                            } else if (isJurisdiction) {
                                colorClasses = 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-purple-200 dark:border-purple-700';
                                textColorClasses = 'text-purple-600 dark:text-purple-300';
                                hoverClasses = 'hover:bg-purple-200 dark:hover:bg-purple-800';
                                typeLabel = 'Juridiction';
                            }

                            return (
                                <span
                                    key={`${filter.type}-${filter.value}-${index}`}
                                    className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${colorClasses} border`}
                                >
                                    <span className={`text-xs ${textColorClasses}`}>
                                        {typeLabel}:
                                    </span>
                                    {filter.label}
                                    <button
                                        onClick={() => removeFilter(filter)}
                                        className={`ml-1 p-0.5 ${hoverClasses} rounded-full transition-colors duration-200`}
                                    >
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </span>
                            );
                        })}

                        {/* ️ BOUTON UNIQUE DE NETTOYAGE */}
                        {(activeFilters.length > 0 || (searchTerm && searchTerm.length > 0)) && (
                            <button
                                onClick={() => {
                                    setActiveFilters([]);
                                    if (searchTerm) handleSearch('');
                                }}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 underline transition-colors duration-200 ml-2 font-medium"
                            >
                                Effacer tout
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Affichage conditionnel : Skeleton pour chargement initial et filtres, Grille normale sinon */}
            {(isInitialLoading || isReplaceLoading) ? (
                <JurisprudenceGridSkeleton />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {jurisprudences
                        .filter(jurisprudence =>
                            !showFollowedOnly || jurisprudence.isFollowed
                        )
                        .map((jurisprudence, index) => (
                            <div
                                key={jurisprudence.id}
                                className="bg-white dark:bg-mcdm-third-300 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md dark:hover:shadow-lg transition-shadow duration-200"
                            >
                                {/* En-tête restructuré */}
                                <div className="mb-4">
                                    <div className="mb-3">
                                        <button
                                            onClick={() => openJurisprudenceModal(jurisprudence.id)}
                                            className="text-left w-full group"
                                        >
                                            <h3 className="font-platypi text-xl font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 group-hover:text-mcdm-green-300 dark:group-hover:text-mcdm-blue-100 transition-colors duration-200 cursor-pointer">
                                                {jurisprudence.date} - {jurisprudence.court}{jurisprudence.chamber ? ` - ${jurisprudence.chamber}` : ''} n° {jurisprudence.number}
                                            </h3>
                                        </button>
                                        <div className="mt-2 space-y-1">
                                            <div>
                                                <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">Juridiction : </span>
                                                <button
                                                    onClick={() => toggleFilter({
                                                        type: 'jurisdiction',
                                                        value: jurisprudence.jurisdiction,
                                                        label: jurisprudence.jurisdiction
                                                    })}
                                                    className={`text-sm transition-all duration-200 hover:scale-105 cursor-pointer px-2 py-1 rounded-md ${isFilterActive('jurisdiction', jurisprudence.jurisdiction)
                                                        ? 'bg-purple-600 text-white ring-1 ring-purple-300 dark:ring-purple-500'
                                                        : 'text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-purple-100 dark:hover:bg-purple-900 hover:text-purple-600 dark:hover:text-purple-300'
                                                        }`}
                                                >
                                                    {jurisprudence.jurisdiction}
                                                </button>
                                            </div>
                                            {jurisprudence.chamber && (
                                                <div>
                                                    <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">Chambre : </span>
                                                    <span className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded-md">
                                                        {jurisprudence.chamber}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bouton Suivre */}
                                    <div className="flex mb-4">
                                        <FollowButton
                                            jurisprudenceId={jurisprudence.id}
                                            initialFollowState={jurisprudence.isFollowed}
                                            onFollowChange={handleFollowChange}
                                        />
                                        {/* 
                                        NOTE: Le FollowButton doit être adapté pour accepter initialFollowState
                                        pour éviter un appel API interne et utiliser directement l'état depuis la grille 
                                        */}
                                    </div>
                                </div>

                                {/* Contenu principal */}
                                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                                    <div className="mb-4">
                                        {/* Solution */}
                                        <div className="mb-4">
                                            <div className="items-left gap-2">
                                                <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">Solution : </span>
                                                <span className={`text-sm font-medium ${getSolutionColor(jurisprudence.solution)}`}>
                                                    {jurisprudence.solution}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Questions principales */}
                                        <div className="mb-4">
                                            <h5 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                                Questions principales
                                            </h5>
                                            <ul className="space-y-1">
                                                {jurisprudence.mainQuestions.length > 0 ? (
                                                    jurisprudence.mainQuestions.slice(0, 3).map((question, qIndex) => (
                                                        <li key={qIndex} className="text-sm text-mcdm-third-500 dark:text-gray-400 flex items-start">
                                                            <span className="w-1.5 h-1.5 bg-mcdm-third-400 dark:bg-mcdm-primary-100 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                                            {question}
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="text-sm text-mcdm-third-500 dark:text-gray-400 italic">
                                                        Aucune question principale spécifiée
                                                    </li>
                                                )}
                                                {jurisprudence.mainQuestions.length > 3 && (
                                                    <li className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                                                        +{jurisprudence.mainQuestions.length - 3} autres questions...
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Domaines juridiques */}
                                        <div className="mb-4">
                                            <h5 className="text-sm font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                                                Domaines juridiques
                                            </h5>
                                            <div className="mb-3">
                                                <button
                                                    onClick={() => toggleFilter({
                                                        type: 'mainDomain',
                                                        value: jurisprudence.mainDomain,
                                                        label: jurisprudence.mainDomain
                                                    })}
                                                    className={`inline-block text-left text-sm px-3 py-2 rounded-md font-medium transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer ${isFilterActive('mainDomain', jurisprudence.mainDomain)
                                                        ? 'bg-blue-600 text-white ring-2 ring-blue-300 dark:ring-blue-500'
                                                        : 'bg-mcdm-blue-100 text-white dark:bg-mcdm-blue-100 dark:text-white hover:bg-blue-500'
                                                        }`}
                                                >
                                                    {jurisprudence.mainDomain}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Sous-domaines */}
                                        <div className="mb-4">
                                            <h5 className="text-sm font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
                                                Sous-domaines
                                            </h5>
                                            <div className="flex flex-wrap gap-2">
                                                {jurisprudence.subDomains.length > 0 ? (
                                                    jurisprudence.subDomains.slice(0, 3).map((domain, dIndex) => (
                                                        <button
                                                            key={`domain-${dIndex}`}
                                                            onClick={() => toggleFilter({
                                                                type: 'subDomain',
                                                                value: domain,
                                                                label: domain
                                                            })}
                                                            className={`inline-block text-sm px-2 py-1.5 rounded-md font-medium transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer ${isFilterActive('subDomain', domain)
                                                                ? 'bg-green-600 text-white ring-2 ring-green-300 dark:ring-green-500'
                                                                : 'bg-mcdm-green-100 text-white dark:bg-mcdm-green-100 dark:text-white hover:bg-green-500'
                                                                }`}
                                                        >
                                                            {domain}
                                                        </button>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-200 italic">
                                                        Aucun sous-domaine spécifié
                                                    </span>
                                                )}
                                                {jurisprudence.subDomains.length > 3 && (
                                                    <span className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                                                        +{jurisprudence.subDomains.length - 3} autres...
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Pied de carte - Affichage informatif uniquement */}
                                        <div className="flex items-center gap-1 text-xs text-mcdm-third-400 dark:text-mcdm-primary-100">
                                            <span className="font-medium">Code :</span>
                                            <span className="font-medium px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded-md">
                                                {jurisprudence.code}
                                            </span>
                                        </div>

                                        {/* Référence du code - Affichage informatif uniquement */}
                                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                            <span className="inline-block text-xs px-2 py-1 rounded font-mono bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                                                {jurisprudence.codeNumber}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            )}

            {/* Bouton "Charger plus" */}
            {hasMore && !loading && (
                <div className="text-center py-8">
                    <button
                        onClick={loadMore}
                        className="px-6 py-3 bg-mcdm-green-400 text-white rounded-xl hover:bg-mcdm-green-500 transition-colors duration-200 font-medium"
                    >
                        Plus de résultats
                    </button>
                </div>
            )}

            {/* Loading pour le chargement de plus de résultats */}
            {loading && !isInitialLoading && !isReplaceLoading && (
                <div className="text-center py-8">
                    <div className="inline-flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-mcdm-blue-100 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-mcdm-third-500 dark:text-mcdm-primary-100">Chargement...</span>
                    </div>
                </div>
            )}

            {/* Messages d'état */}
            {jurisprudences.filter(j => !showFollowedOnly || j.isFollowed).length === 0 && !loading && !isInitialLoading && !isReplaceLoading && searchTerm && (
                <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-2">
                        {showFollowedOnly ? 'Aucune jurisprudence suivie trouvée' : 'Aucune jurisprudence trouvée'}
                    </p>
                </div>
            )}

            {showFollowedOnly && jurisprudences.filter(j => j.isFollowed).length === 0 && !loading && !isInitialLoading && !isReplaceLoading && !searchTerm && (
                <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-2">
                        Aucune jurisprudence suivie
                    </p>
                </div>
            )}

            {!hasMore && jurisprudences.length > 0 && (
                <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Vous avez consulté toutes les jurisprudences disponibles.
                    </p>
                </div>
            )}

            {jurisprudences.length === 0 && !loading && !isInitialLoading && !isReplaceLoading && !searchTerm && !showFollowedOnly && (
                <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Utilisez la barre de recherche pour découvrir des jurisprudences pertinentes.
                    </p>
                </div>
            )}

            {/* Modal de détails de jurisprudence */}
            <JurisprudenceModalWrapper
                jurisprudenceId={selectedJurisprudenceId}
                isOpen={isModalOpen}
                onClose={closeJurisprudenceModal}
                onFollowChange={handleFollowChange}
                initialFollowState={jurisprudences.find(j => j.id === selectedJurisprudenceId)?.isFollowed}
            />

            {/* Modal de filtres */}
            <FiltersModal
                isOpen={isFiltersModalOpen}
                onClose={() => setIsFiltersModalOpen(false)}
                activeFilters={activeFilters}
                onFiltersChange={setActiveFilters}
            />
        </div>
    );
};

export default JurisprudencesSearch;